/**
 * BrandingContext (web) — Owner-editable brand text & images, mirrors the
 * mobile BrandingContext exactly. Fetches /api/branding on mount,
 * exposes useBranding() and owner-only mutators.
 *
 * Per user directive: the owner can ALWAYS access edit affordances on
 * every page where a logo or brand text appears. Non-owners (customers,
 * drivers, admins, anonymous) see nothing extra.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CACHE_KEY = "branding_cache_v1";

// Mirror defaults from /app/backend/routes/branding.py
const FALLBACK = {
  app_name: "Jiffys",
  app_tagline: "Roadside Assistance",
  logo_letter: "J",
  logo_image_url: "",
  splash_color: "#FF3008",
  primary_color: "#FF3008",
  login_title: "Sign In",
  register_cta: "Create an account",
  customer_greeting_sub: "What do you need today?",
  menu_roadside_title: "Roadside Assistance",
  menu_roadside_sub: "Immediate dispatch · Pay now",
  menu_mechanic_title: "Mobile Mechanic",
  menu_mechanic_sub: "Schedule 24hrs ahead · Pay after service",
  menu_delivery_title: "Get it Delivered",
  menu_delivery_sub: "Food, groceries & more · Pay now",
  mechanic_early_note:
    "Need it sooner? Opt-in for earlier availability at checkout.",
  provider_status_online: "Online",
  provider_status_paused: "Paused",
  provider_status_offline: "Offline",
  provider_earnings_label: "This Dash",
  provider_dash_now: "Dash Now",
  provider_dash_sub: "Start receiving job offers",
  provider_finding_offers: "Finding offers...",
  emergency_phone: "2602183536",
  emergency_phone_display: "(260) 218-3536",
  support_email: "support@jiffysassistance.site",
  marketing_blurb:
    "On-demand roadside help & mobile mechanics — fast, reliable, right to your location.",
  // Web-only extras
  landing_hero_title: "Roadside Help in Minutes",
  landing_hero_sub:
    "Mobile mechanics, jump-starts, lockouts, fuel, tows — dispatched fast to your exact location.",
  landing_cta_get_help: "Get Help Now",
  landing_cta_become_provider: "Become a Provider",
  footer_company_name: "Jiffys Assistance",
  footer_location: "Fort Wayne, IN",
  footer_copyright: "© 2026 Jiffys Assistance. All rights reserved.",
};

const BrandingContext = createContext(null);

function resolveImage(urlOrPath) {
  if (!urlOrPath) return "";
  if (urlOrPath.startsWith("http://") || urlOrPath.startsWith("https://")) {
    return urlOrPath;
  }
  if (urlOrPath.startsWith("/api/")) {
    return `${BACKEND_URL}${urlOrPath}`;
  }
  return urlOrPath;
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(FALLBACK);
  const [defaults, setDefaults] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);

  const fetchBranding = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/branding`);
      const merged = { ...FALLBACK, ...(res.data.branding || {}) };
      setBranding(merged);
      setDefaults({ ...FALLBACK, ...(res.data.defaults || {}) });
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(merged));
      } catch {
        /* quota or denied */
      }
    } catch (e) {
      console.warn("[branding] fetch failed:", e?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Hydrate from localStorage cache first, then fetch
  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === "object") {
          setBranding({ ...FALLBACK, ...parsed });
        }
      }
    } catch {
      /* ignore */
    }
    fetchBranding();
  }, [fetchBranding]);

  const get = useCallback(
    (key, fallback) => {
      const v = branding[key];
      if (typeof v === "string" && v.length > 0) return v;
      if (typeof fallback === "string") return fallback;
      return FALLBACK[key] ?? "";
    },
    [branding]
  );

  const getAuthToken = () => localStorage.getItem("token");

  const updateText = useCallback(async (key, value) => {
    const token = getAuthToken();
    if (!token) return { ok: false, error: "Not signed in" };
    try {
      const res = await axios.put(
        `${API}/branding`,
        { key, value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBranding((prev) => ({ ...prev, [key]: value }));
      return { ok: true, data: res.data };
    } catch (e) {
      return {
        ok: false,
        error: e?.response?.data?.detail || e?.message || "Update failed",
      };
    }
  }, []);

  const uploadImage = useCallback(async (key, file) => {
    const token = getAuthToken();
    if (!token) return { ok: false, error: "Not signed in" };
    const fd = new FormData();
    fd.append("key", key);
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/branding/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranding((prev) => ({ ...prev, [key]: res.data.url }));
      return { ok: true, url: res.data.url };
    } catch (e) {
      return {
        ok: false,
        error: e?.response?.data?.detail || e?.message || "Upload failed",
      };
    }
  }, []);

  const resetKey = useCallback(
    async (key) => {
      const token = getAuthToken();
      if (!token) return { ok: false, error: "Not signed in" };
      try {
        await axios.post(
          `${API}/branding/reset`,
          { key, value: "" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setBranding((prev) => ({
          ...prev,
          [key]: defaults[key] ?? FALLBACK[key] ?? "",
        }));
        return { ok: true };
      } catch (e) {
        return {
          ok: false,
          error: e?.response?.data?.detail || e?.message || "Reset failed",
        };
      }
    },
    [defaults]
  );

  const value = useMemo(
    () => ({
      branding,
      defaults,
      loading,
      get,
      resolveImage,
      updateText,
      uploadImage,
      resetKey,
      refresh: fetchBranding,
    }),
    [branding, defaults, loading, get, updateText, uploadImage, resetKey, fetchBranding]
  );

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error("useBranding must be used within BrandingProvider");
  }
  return ctx;
}
